/**
 * Cache utilities for managing sessionStorage with timestamp validation
 */

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Check if cached data is still valid
 * @param {number} timestamp - The timestamp when data was cached
 * @returns {boolean} True if cache is still valid
 */
export function isCacheValid(timestamp) {
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_DURATION;
}

/**
 * Get cached data from sessionStorage with validation
 * @param {string} key - The cache key
 * @returns {any} Parsed cached data or null if invalid/missing
 */
export function getCachedData(key) {
  try {
    const cached = sessionStorage.getItem(key);
    const timestamp = sessionStorage.getItem(`${key}_timestamp`);

    if (cached && isCacheValid(parseInt(timestamp, 10))) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn(`Failed to get cached data for ${key}:`, error);
  }
  return null;
}

/**
 * Set data in sessionStorage with timestamp
 * @param {string} key - The cache key
 * @param {any} data - Data to cache (will be JSON stringified)
 * @returns {boolean} True if successful
 */
export function setCachedData(key, data) {
  try {
    const timestamp = Date.now().toString();
    sessionStorage.setItem(key, JSON.stringify(data));
    sessionStorage.setItem(`${key}_timestamp`, timestamp);
    return true;
  } catch (error) {
    console.warn(`Failed to cache data for ${key}:`, error);
    return false;
  }
}

/**
 * Get cached data with default value
 * @param {string} key - The cache key
 * @param {any} defaultValue - Default value if cache is invalid
 * @returns {any} Cached data or default value
 */
export function getCachedDataOr(key, defaultValue) {
  const cached = getCachedData(key);
  return cached !== null ? cached : defaultValue;
}

/**
 * Check if cache exists and is valid for a key
 * @param {string} key - The cache key
 * @returns {boolean} True if valid cache exists
 */
export function hasValidCache(key) {
  try {
    const cached = sessionStorage.getItem(key);
    const timestamp = sessionStorage.getItem(`${key}_timestamp`);
    return cached && isCacheValid(parseInt(timestamp, 10));
  } catch {
    return false;
  }
}

/**
 * Initialize state with cached data
 * @param {string} key - The cache key
 * @param {any} defaultValue - Default value if no valid cache
 * @returns {any} Cached data or default value
 */
export function initializeWithCache(key, defaultValue) {
  const cached = getCachedData(key);
  return cached !== null ? cached : defaultValue;
}

/**
 * Remove cached data
 * @param {string} key - The cache key
 */
export function removeCachedData(key) {
  try {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(`${key}_timestamp`);
  } catch (error) {
    console.warn(`Failed to remove cached data for ${key}:`, error);
  }
}
